import { sortByDescendingShortSide, getPossibleOrientations, applySortStrategy, pruneContainedRects } from './common.js';

/**
 * Split free rectangles after placing a piece (MaxRects style)
 */
const splitFreeRects = (freeRects, placedPiece, cutWidth) => {
  const newFreeRects = [];
  const pieceRight = placedPiece.x + placedPiece.width + cutWidth;
  const pieceBottom = placedPiece.y + placedPiece.height + cutWidth;

  for (const rect of freeRects) {
    if (placedPiece.x >= rect.x + rect.width || pieceRight <= rect.x ||
        placedPiece.y >= rect.y + rect.height || pieceBottom <= rect.y) {
      newFreeRects.push(rect);
    } else {
      if (placedPiece.x > rect.x) {
        const leftWidth = placedPiece.x - rect.x - cutWidth;
        if (leftWidth > cutWidth) newFreeRects.push({ x: rect.x, y: rect.y, width: leftWidth, height: rect.height });
      }
      if (pieceRight < rect.x + rect.width) {
        const rightWidth = rect.x + rect.width - pieceRight;
        if (rightWidth > cutWidth) newFreeRects.push({ x: pieceRight, y: rect.y, width: rightWidth, height: rect.height });
      }
      if (placedPiece.y > rect.y) {
        const topHeight = placedPiece.y - rect.y - cutWidth;
        if (topHeight > cutWidth) newFreeRects.push({ x: rect.x, y: rect.y, width: rect.width, height: topHeight });
      }
      if (pieceBottom < rect.y + rect.height) {
        const bottomHeight = rect.y + rect.height - pieceBottom;
        if (bottomHeight > cutWidth) newFreeRects.push({ x: rect.x, y: pieceBottom, width: rect.width, height: bottomHeight });
      }
    }
  }
  return newFreeRects;
};

/**
 * Find best BSSF placement for a piece
 */
const findBSSFPlacement = (piece, freeRects, cutWidth) => {
  const orientations = getPossibleOrientations(piece);
  let best = null;

  for (const orientation of orientations) {
    for (let j = 0; j < freeRects.length; j++) {
      const rect = freeRects[j];
      if (orientation.width <= rect.width && orientation.height <= rect.height) {
        const leftoverH = rect.width - orientation.width;
        const leftoverV = rect.height - orientation.height;
        const shortSide = Math.min(leftoverH, leftoverV);
        const longSide = Math.max(leftoverH, leftoverV);
        if (!best || shortSide < best.shortSide || (shortSide === best.shortSide && longSide < best.longSide)) {
          best = { rect, rectIndex: j, orientation, shortSide, longSide };
        }
      }
    }
  }
  return best;
};

/**
 * Maximal Rectangles BSSF-DESCSS-BBF algorithm
 * BUG FIX: tracks placed piece indices explicitly instead of slicing by count
 */
export const maximalRectanglesOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  let remainingPieces = sortStrategy ? applySortStrategy(allPieces, sortStrategy) : sortByDescendingShortSide(allPieces);
  const bins = [];
  const placedIndices = new Set();

  for (let i = 0; i < remainingPieces.length; i++) {
    const piece = remainingPieces[i];
    let bestBinIndex = -1, bestPlacement = null;

    for (let binIdx = 0; binIdx < bins.length; binIdx++) {
      const placement = findBSSFPlacement(piece, bins[binIdx].freeRects, cutWidth);
      if (placement) {
        if (!bestPlacement || placement.shortSide < bestPlacement.shortSide ||
            (placement.shortSide === bestPlacement.shortSide && placement.longSide < bestPlacement.longSide)) {
          bestPlacement = placement; bestBinIndex = binIdx;
        }
      }
    }

    if (!bestPlacement) {
      const newBin = {
        id: bins.length + 1, width: sheetWidth, height: sheetHeight, pieces: [],
        freeRects: [{ x: 0, y: 0, width: sheetWidth, height: sheetHeight }]
      };
      bins.push(newBin);
      const placement = findBSSFPlacement(piece, newBin.freeRects, cutWidth);
      if (placement) { bestPlacement = placement; bestBinIndex = bins.length - 1; }
    }

    if (bestPlacement) {
      const bin = bins[bestBinIndex];
      const placedPiece = { ...bestPlacement.orientation, x: bestPlacement.rect.x, y: bestPlacement.rect.y };
      bin.pieces.push(placedPiece);
      placedIndices.add(i);
      bin.freeRects = splitFreeRects(bin.freeRects, placedPiece, cutWidth);
      bin.freeRects = pruneContainedRects(bin.freeRects);
    }
  }

  // BACKFILL: try to move pieces from later bins into earlier bins
  if (bins.length > 1) {
    for (let laterIdx = bins.length - 1; laterIdx >= 1; laterIdx--) {
      const laterBin = bins[laterIdx];
      const keptPieces = [];
      for (const piece of laterBin.pieces) {
        let moved = false;
        for (let earlyIdx = 0; earlyIdx < laterIdx; earlyIdx++) {
          const placement = findBSSFPlacement(piece, bins[earlyIdx].freeRects, cutWidth);
          if (placement) {
            const placedPiece = { ...placement.orientation, x: placement.rect.x, y: placement.rect.y };
            bins[earlyIdx].pieces.push(placedPiece);
            bins[earlyIdx].freeRects = splitFreeRects(bins[earlyIdx].freeRects, placedPiece, cutWidth);
            bins[earlyIdx].freeRects = pruneContainedRects(bins[earlyIdx].freeRects);
            moved = true;
            break;
          }
        }
        if (!moved) keptPieces.push(piece);
      }
      laterBin.pieces = keptPieces;
    }
  }

  const layouts = bins.filter(b => b.pieces.length > 0).map((bin, idx) => ({
    id: idx + 1, width: bin.width, height: bin.height, pieces: bin.pieces
  }));

  const unplacedPieces = remainingPieces.filter((_, i) => !placedIndices.has(i));

  return { layouts, unplacedPieces, unplacedCount: unplacedPieces.length };
};
